import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import {
  Button,
  TextField,
  Dialog,
  makeStyles,
  CircularProgress,
  Typography,
  IconButton,
  Box,
} from "@material-ui/core";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";

import CloseIcon from "@material-ui/icons/Close";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  grupoTitulo: {
    fontSize: "0.68rem",
    fontWeight: 800,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    lineHeight: "30px",
    backgroundColor: theme.palette.type === "dark" ? "#1b2320" : "#f8fafc",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  opcao: {
    padding: "6px 14px",
  },
  opcaoLinha: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    minWidth: 0,
  },
  opcaoInicial: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.75rem",
    fontWeight: 800,
    flexShrink: 0,
  },
  opcaoNome: {
    flex: 1,
    minWidth: 0,
    fontSize: "0.9rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  opcaoStatus: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  dialogPaper: {
    borderRadius: "16px",
    width: "100%",
    maxWidth: 520,
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 20px 50px rgba(0, 0, 0, 0.15)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.06)"
    }`,
    overscrollBehavior: "contain",
  },
  dialogTitle: {
    padding: theme.spacing(2, 3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "#f0f2f5"
    }`,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.02)"
        : "#ffffff",
    flexShrink: 0,
  },
  headerIconWrapper: {
    width: 42,
    height: 42,
    borderRadius: "12px",
    background:
      "linear-gradient(135deg, rgba(30, 152, 85, 0.15) 0%, rgba(30, 152, 85, 0.04) 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 14,
    color: "#1E9855",
    border: "1px solid rgba(30, 152, 85, 0.2)",
  },
  titleText: {
    fontWeight: 700,
    fontSize: "1.15rem",
    color: theme.palette.text.primary,
  },
  subtitleText: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    marginTop: 1,
  },
  closeBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    borderRadius: "10px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.05)",
      color: theme.palette.text.primary,
    },
  },
  dialogContent: {
    padding: theme.spacing(3),
    overflowY: "auto",
    overscrollBehavior: "contain",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
    "&::-webkit-scrollbar": {
      width: "5px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(30, 152, 85, 0.25)",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "rgba(30, 152, 85, 0.55)",
    },
  },
  fieldCard: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  fieldHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: "0.85rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  fieldIcon: {
    fontSize: 18,
    color: "#1E9855",
  },
  customInputRoot: {
    borderRadius: "10px",
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.04)" : "#f8fafc",
    "& fieldset": {
      borderColor:
        theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.12)" : "#e2e8f0",
      transition: "all 0.2s ease",
    },
    "&:hover fieldset": {
      borderColor: "#1E9855",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1E9855",
      borderWidth: "1.5px",
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "#f0f2f5"
    }`,
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1.5),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.02)"
        : "#ffffff",
    flexShrink: 0,
  },
  btnCancel: {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    padding: "8px 18px",
    color: theme.palette.text.secondary,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.12)"
        : "#d0d5dd"
    }`,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
      borderColor: theme.palette.text.primary,
    },
  },
  btnSave: {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    padding: "8px 24px",
    backgroundColor: "#1E9855",
    color: "#ffffff",
    boxShadow: "0 2px 8px rgba(30, 152, 85, 0.25)",
    "&:hover": {
      backgroundColor: "#17803f",
      boxShadow: "0 4px 14px rgba(30, 152, 85, 0.35)",
    },
  },
  confirmaBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 14,
    padding: theme.spacing(1, 0),
  },
  confirmaFluxo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    width: "100%",
  },
  confirmaPessoa: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
    maxWidth: 170,
  },
  confirmaAvatar: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.2rem",
    fontWeight: 800,
  },
  confirmaRotulo: {
    fontSize: "0.68rem",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  confirmaNome: {
    fontSize: "0.92rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  confirmaSeta: {
    color: "#1E9855",
    fontSize: 28,
    flexShrink: 0,
  },
  confirmaTexto: {
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    lineHeight: 1.5,
  },
  confirmaCliente: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.8rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#f1f5f9",
    borderRadius: 20,
    padding: "4px 12px",
  },
}));

const filterOptions = createFilterOptions({
  trim: true,
  // Busca pelo nome (sem o sufixo do grupo).
  stringify: (option) => option.name || "",
});

// Separacao da lista de destino: vendedores primeiro (e quem mais recebe),
// depois operadores (formalizacao) e por ultimo administradores.
const GRUPOS = {
  user: { ordem: 0, titulo: "Vendedores", cor: "#2563eb", fundo: "#eff6ff" },
  operador: { ordem: 1, titulo: "Operadores", cor: "#ea580c", fundo: "#fff7ed" },
  admin: { ordem: 2, titulo: "Administradores", cor: "#dc2626", fundo: "#fef2f2" },
};
const grupoDe = (u) => GRUPOS[u?.profile] || GRUPOS.user;

const STATUS = {
  disponivel: { rotulo: "Disponível", cor: "#22c55e" },
  almoco: { rotulo: "Almoço", cor: "#f59e0b" },
  ausente: { rotulo: "Ausente", cor: "#ef4444" },
};

// So da para transferir para quem esta "Disponivel". Admin nao tem status.
const podeReceber = (u) =>
  !!u && (u.profile === "admin" || (u.disponibilidade || "disponivel") === "disponivel");

const TransferTicketModal = ({ modalOpen, onClose, ticketid, ticket }) => {
  const history = useHistory();
  const classes = useStyles();

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  // Passo de confirmacao: antes de transferir, mostra de quem -> para quem.
  const [confirmando, setConfirmando] = useState(false);

  // Quem esta com o cliente agora (para a confirmacao).
  const origem = ticket?.user?.name
    ? { nome: ticket.user.name, rotulo: "Atendente atual", ia: false }
    : ticket?.userId
    ? { nome: "Atendente atual", rotulo: "Atendente atual", ia: false }
    : { nome: "Assistente (I.A)", rotulo: "Hoje com", ia: true };
  const nomeCliente = ticket?.contact?.name || "este cliente";

  // A conexao do atendimento nao se troca aqui: isso e o "Trocar conexao" do
  // menu do atendimento (TrocarConexaoModal), para nao haver dois caminhos
  // para a mesma coisa.
  useEffect(() => {
    if (!modalOpen) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(
      () => {
        const fetchUsers = async () => {
          try {
            const { data } = await api.get("/users/", {
              params: { searchParam },
            });
            
            // Admin tambem atende. Ele so nao recebe a saudacao automatica
            // (isso e tratado no UpdateTicketService), mas pode assumir o
            // cliente normalmente.
            setOptions(
              [...(data.users || [])].sort(
                (a, b) =>
                  grupoDe(a).ordem - grupoDe(b).ordem ||
                  String(a.name || "").localeCompare(String(b.name || ""), "pt-BR")
              )
            );
            setLoading(false);
          } catch (err) {
            setLoading(false);
            toastError(err);
          }
        };

        fetchUsers();
      },
      searchParam ? 300 : 0
    );
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setSelectedUser(null);
    setConfirmando(false);
  };

  const handleSaveTicket = async (e) => {
    e.preventDefault();
    if (!ticketid || !selectedUser || !podeReceber(selectedUser)) return;
    // 1o clique: so pede confirmacao. 2o clique (Sim, transferir): transfere.
    if (!confirmando) {
      setConfirmando(true);
      return;
    }
    setLoading(true);
    try {
      let data = {};

      
      if (selectedUser) {
        data.userId = selectedUser.id;
        data.status = "open";
        // o backend confere de novo se a pessoa continua Disponivel
        data.transferencia = true;
      }














      await api.put(`/tickets/${ticketid}`, data);

      setLoading(false);
      setConfirmando(false);
      history.push(`/atendimentos`);
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  return (
    <Dialog
      open={modalOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <form onSubmit={handleSaveTicket}>
        {}
        <div className={classes.dialogTitle}>
          <Box display="flex" alignItems="center">
            <div className={classes.headerIconWrapper}>
              <SwapHorizIcon style={{ fontSize: 24 }} />
            </div>
            <div>
              <Typography className={classes.titleText}>
                Transferir Atendimento
              </Typography>
              <Typography className={classes.subtitleText}>
                {confirmando ? "Confirme a transferência" : "Selecione o atendente de destino"}
              </Typography>
            </div>
          </Box>
          <IconButton className={classes.closeBtn} onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {}
        <div className={classes.dialogContent}>
          {confirmando && selectedUser ? (
            <div className={classes.confirmaBox}>
              <div className={classes.confirmaFluxo}>
                <div className={classes.confirmaPessoa}>
                  <span className={classes.confirmaRotulo}>{origem.rotulo}</span>
                  <span
                    className={classes.confirmaAvatar}
                    style={origem.ia
                      ? { backgroundColor: "#f5f3ff", color: "#7c3aed" }
                      : { backgroundColor: grupoDe(ticket?.user).fundo, color: grupoDe(ticket?.user).cor }}
                  >
                    {origem.ia ? "IA" : String(origem.nome || "?").charAt(0).toUpperCase()}
                  </span>
                  <span className={classes.confirmaNome} title={origem.nome}>{origem.nome}</span>
                </div>
                <SwapHorizIcon className={classes.confirmaSeta} />
                <div className={classes.confirmaPessoa}>
                  <span className={classes.confirmaRotulo}>Vai para</span>
                  <span
                    className={classes.confirmaAvatar}
                    style={{ backgroundColor: grupoDe(selectedUser).fundo, color: grupoDe(selectedUser).cor }}
                  >
                    {String(selectedUser.name || "?").charAt(0).toUpperCase()}
                  </span>
                  <span className={classes.confirmaNome} title={selectedUser.name}>{selectedUser.name}</span>
                </div>
              </div>
              <span className={classes.confirmaCliente}>
                <PersonOutlineIcon style={{ fontSize: 16 }} /> {nomeCliente}
              </span>
              <Typography className={classes.confirmaTexto}>
                Tem certeza que deseja transferir este atendimento
                {origem.ia ? " da I.A" : ""} para <b>{selectedUser.name}</b>?
              </Typography>
            </div>
          ) : (
          <div className={classes.fieldCard}>
            <div className={classes.fieldHeader}>
              <PersonOutlineIcon className={classes.fieldIcon} />
              <span>Transferir para Atendente</span>
            </div>
            <Autocomplete
              fullWidth
              getOptionLabel={(option) => option?.name || ""}
              groupBy={(option) => grupoDe(option).titulo}
              classes={{ groupLabel: classes.grupoTitulo, option: classes.opcao }}
              renderOption={(option) => {
                const g = grupoDe(option);
                const st =
                  option.profile === "admin"
                    ? null
                    : STATUS[option.disponibilidade || "disponivel"] || STATUS.disponivel;
                return (
                  <div className={classes.opcaoLinha}>
                    <span
                      className={classes.opcaoInicial}
                      style={{ backgroundColor: g.fundo, color: g.cor }}
                    >
                      {String(option.name || "?").charAt(0).toUpperCase()}
                    </span>
                    <span className={classes.opcaoNome}>{option.name}</span>
                    {st && (
                      <span
                        className={classes.opcaoStatus}
                        title={podeReceber(option) ? st.rotulo : `${st.rotulo} — não pode receber agora`}
                        style={podeReceber(option) ? undefined : { color: st.cor, fontWeight: 700 }}
                      >
                        <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: st.cor }} />
                        {st.rotulo}
                      </span>
                    )}
                  </div>
                );
              }}
              value={selectedUser}
              onChange={(e, newValue) => setSelectedUser(podeReceber(newValue) ? newValue : null)}
              getOptionDisabled={(option) => !podeReceber(option)}
              options={options}
              filterOptions={filterOptions}
              autoHighlight
              openOnFocus
              noOptionsText="Nenhum atendente encontrado"
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Selecione ou busque o atendente..."
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    className: classes.customInputRoot,
                    endAdornment: (
                      <React.Fragment>
                        {loading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                  onChange={(e) => setSearchParam(e.target.value)}
                />
              )}
            />
            <Typography style={{ fontSize: "0.75rem", marginTop: 8, opacity: 0.7 }}>
              Só é possível transferir para quem está <b style={{ color: "#1E9855" }}>Disponível</b>.
              {" "}Quem está em almoço ou ausente aparece bloqueado.
            </Typography>
          </div>
          )}

        </div>

        {}
        <div className={classes.dialogActions}>
          <Button
            onClick={confirmando ? () => setConfirmando(false) : handleClose}
            disabled={loading}
            className={classes.btnCancel}
          >
            {confirmando ? "Voltar" : "Cancelar"}
          </Button>
          <ButtonWithSpinner
            variant="contained"
            type="submit"
            loading={loading}
            disabled={!selectedUser || !podeReceber(selectedUser)}
            className={classes.btnSave}
          >
            {confirmando ? "Sim, transferir" : "Transferir Atendimento"}
          </ButtonWithSpinner>
        </div>
      </form>
    </Dialog>
  );
};

export default TransferTicketModal;
